<template>
  <a-modal
    v-model:visible="visible"
    :title="t('workspaceSettings.identifiers.historyModal.title')"
    @cancel="handleCancel"
    :footer="false"
    width="800px"
  >
    <a-table :data="historyList" :loading="loading" :pagination="false">
      <template #columns>
        <a-table-column
          :title="
            t('workspaceSettings.identifiers.historyModal.targetWorkflow')
          "
          data-index="target_workflow_name"
        >
          <template #cell="{ record }">
            <div class="wf-info">
              <icon-branch />
              <span>{{
                record.target_workflow_name ||
                t('workspaceSettings.identifiers.targetModal.unknownWorkflow')
              }}</span>
            </div>
          </template>
        </a-table-column>
        <a-table-column
          :title="t('workspaceSettings.identifiers.historyModal.targetVersion')"
          data-index="target_publish_version"
        >
          <template #cell="{ record }">
            <a-tag color="green" size="small">{{
              record.target_publish_version
            }}</a-tag>
          </template>
        </a-table-column>
        <a-table-column
          :title="t('workspaceSettings.identifiers.historyModal.updatedAt')"
          data-index="updated_at"
        >
          <template #cell="{ record }">
            {{ formatDate(record.updated_at) }}
          </template>
        </a-table-column>
        <a-table-column
          :title="t('workspaceSettings.identifiers.historyModal.updatedBy')"
          data-index="updated_by"
        ></a-table-column>
      </template>
    </a-table>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { IconBranch } from '@arco-design/web-vue/es/icon';
  import dayjs from 'dayjs';
  import { useI18n } from 'vue-i18n';
  import { getProtocolHistoryApi } from '@/api/protocol';
  import type { ProtocolItem, ProtocolHistoryItem } from '@/types/protocol';
  import { useUserStore } from '@/store';

  const props = defineProps<{
    modelValue: boolean;
    data: ProtocolItem | null;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
  }>();

  const userStore = useUserStore();
  const { t } = useI18n();
  const loading = ref(false);
  const historyList = ref<ProtocolHistoryItem[]>([]);

  const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const formatDate = (date: string) => {
    if (!date) return '-';
    return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
  };

  const fetchHistory = async () => {
    if (!props.data) return;
    loading.value = true;
    try {
      const res = await getProtocolHistoryApi({
        name: props.data.name,
        tenant_uuid: userStore.tenantUuid,
        workspace_uuid: userStore.workspaceUuid,
      });
      if (res.data?.results) {
        historyList.value = res.data.results;
      }
    } catch (error) {
      console.error(error);
    } finally {
      loading.value = false;
    }
  };

  watch(
    () => props.modelValue,
    (val) => {
      if (val) {
        fetchHistory();
      }
    }
  );

  const handleCancel = () => {
    visible.value = false;
  };
</script>

<style lang="less" scoped>
  .wf-info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text-1);
  }
</style>
